import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { LineaDevolvible, OrdenDetalle } from '@manejo-alcanza/contracts';
import {
  cantidad,
  crearMotivoDevolucion,
  devolucionesDeOrden,
  fecha,
  pesos,
  quienSoy,
  registrarDevolucion,
} from '../api';
import { Aviso, Boton, Card, Error_ } from './basicos';

/**
 * Etapa 7 — las devoluciones, dentro del expediente de la orden.
 *
 * Va aquí y no en una pantalla propia porque **una devolución no se entiende
 * sola**: lo primero que hace falta saber es de qué orden es, qué se recibió y
 * cuánto queda por devolver. Es el mismo sitio y el mismo criterio que
 * `PanelRecepcion`.
 *
 * ## Lo que esta pantalla dice en letra, y no calla
 *
 * - **Que la entrega no cambia.** Es la duda que le va a entrar a cualquiera al
 *   registrar una: «¿esto me va a descuadrar la orden?». No.
 * - **Que si el producto vuelve a la bodega lo decide el motivo.** No hay
 *   casilla que marcar: se elige el porqué y el resto se deriva.
 * - **Que sin motivos no se puede registrar**, quién puede crearlos y cómo. Un
 *   desplegable vacío sin explicación sería un callejón sin salida.
 */
export function PanelDevoluciones({ orden }: { orden: OrdenDetalle }): ReactNode {
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);

  const datos = useQuery({
    queryKey: ['devoluciones', orden.id],
    queryFn: () => devolucionesDeOrden(orden.id),
  });

  /*
   * El usuario sale de la cache de la sesion, no de una prop.
   *
   * `App` ya pregunto `GET /auth/yo` con esta misma llave, asi que esto no es
   * una peticion mas: es leer lo que ya esta. Pasarlo como prop obligaria a
   * enhebrarlo por la ruta y el detalle, que no lo necesitan para nada.
   *
   * Y **esto no es la autorizacion**: el backend responde 403 igual. Es que un
   * boton que va a dar 403 es peor que no tenerlo.
   */
  const sesion = useQuery({ queryKey: ['yo'], queryFn: quienSoy });

  // Una orden anulada no se pidió nunca: no hay nada que devolver de ella.
  if (orden.estado === 'ANULADA') return null;

  const rol = sesion.data?.rol;
  const puedeRegistrar =
    rol === 'ADMINISTRADOR' || rol === 'COMERCIAL_FINANCIERO' || rol === 'REPARTIDOR';

  const d = datos.data;
  const yaHay = d?.devoluciones ?? [];
  const total = yaHay.reduce((t, x) => t + Number(x.valorTotal), 0);

  return (
    <Card titulo="Devoluciones">
      {yaHay.length === 0 ? (
        <p className="text-sm text-tenue">No se ha devuelto nada de esta orden.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {yaHay.map((x) => (
              <li key={x.id} className="rounded-md border border-borde px-3 py-2 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span>
                    <strong>{fecha(x.fechaDevolucion)}</strong> · {x.motivoNombre}
                  </span>
                  <span className="cifra font-semibold text-alerta">−{pesos(x.valorTotal)}</span>
                </div>
                <div className="mt-1 text-xs text-tenue">
                  {x.lineas.map((l) => `${cantidad(l.cantidad)} × ${l.producto}`).join(' · ')}
                </div>
                <div className="mt-1 text-xs text-tenue">
                  {x.reentraABodega
                    ? 'El producto volvió a la bodega.'
                    : 'El producto no volvió a la bodega.'}
                  {x.documentoSoporte && ` · soporte ${x.documentoSoporte}`}
                  {x.registradaPor && ` · lo registró ${x.registradaPor}`}
                </div>
              </li>
            ))}
          </ul>

          {/*
            El recordatorio va debajo de la lista, donde aparece la duda: quien
            acaba de ver «−$150.000» en una orden ENTREGADA se pregunta si algo
            quedó mal. No quedó.
          */}
          <p className="mt-3 text-xs text-tenue">
            Devuelto en total: <strong className="cifra">{pesos(total.toFixed(2))}</strong>. La
            entrega sigue siendo la que fue — una devolución no cambia el estado de la orden;
            baja el ingreso <strong>del mes en que ocurrió</strong>.
          </p>
        </>
      )}

      {puedeRegistrar && d && (
        <div className="mt-4 border-t border-borde pt-4">
          {!d.tieneRecepcion ? (
            <Aviso>
              Esta orden todavía no tiene registrado qué recibió el cliente, así que no consta
              que haya llegado nada. Registra primero la recepción.
            </Aviso>
          ) : d.motivos.length === 0 ? (
            <SinMotivos
              esAdministrador={rol === 'ADMINISTRADOR'}
              alCrear={() => void cliente.invalidateQueries({ queryKey: ['devoluciones'] })}
            />
          ) : abierto ? (
            <Formulario
              datos={d}
              alTerminar={() => {
                setAbierto(false);
                void cliente.invalidateQueries({ queryKey: ['devoluciones'] });
                void cliente.invalidateQueries({ queryKey: ['orden', orden.id] });
              }}
              alCancelar={() => setAbierto(false)}
            />
          ) : (
            <Boton onClick={() => setAbierto(true)}>Registrar una devolución</Boton>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * El catálogo está vacío, y eso es correcto.
 *
 * **No se resuelve con un desplegable vacío**: se dice qué falta, quién lo puede
 * arreglar, y —si quien mira es esa persona— se le da el atajo aquí mismo.
 */
function SinMotivos({
  esAdministrador,
  alCrear,
}: {
  esAdministrador: boolean;
  alCrear: () => void;
}): ReactNode {
  const [nombre, setNombre] = useState('');
  const [reentra, setReentra] = useState(false);

  const crear = useMutation({
    mutationFn: () => crearMotivoDevolucion({ nombre, reentraABodega: reentra }),
    onSuccess: () => {
      setNombre('');
      setReentra(false);
      alCrear();
    },
  });

  return (
    <div className="space-y-3">
      <Aviso>
        <strong>No hay motivos de devolución todavía.</strong> El sistema no se los inventa: los
        motivos reales de este negocio los sabe quien los ve llegar.{' '}
        {esAdministrador
          ? 'Crea el primero aquí abajo.'
          : 'Pídele al administrador que cree los que hagan falta.'}
      </Aviso>

      {esAdministrador && (
        <div className="space-y-2 rounded-md border border-borde p-3">
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Por ejemplo: Producto averiado"
            className="min-h-[44px] w-full rounded-md border border-borde px-2 text-sm sm:min-h-0 sm:py-1.5"
          />
          {/*
            Esta casilla es el dato que hace funcionar todo lo demás: de aquí
            sale si el libro de inventario recibe el producto de vuelta. Se
            declara UNA VEZ, al crear el motivo, y no en cada devolución — en
            cada devolución sería una casilla que alguien marca con prisa sobre
            una avería.
          */}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={reentra}
              onChange={(e) => setReentra(e.target.checked)}
              className="mt-1"
            />
            <span>
              Con este motivo, <strong>el producto vuelve a la bodega</strong> en estado vendible.
              <span className="block text-xs text-tenue">
                Marca esto solo si se puede volver a vender. De aquí sale si el inventario lo
                recibe de vuelta, así que no hay que decidirlo otra vez en cada devolución.
              </span>
            </span>
          </label>
          <Boton
            deshabilitado={nombre.trim().length < 3 || crear.isPending}
            onClick={() => crear.mutate()}
          >
            Crear el motivo
          </Boton>
          {crear.isError && <Error_>{(crear.error as Error).message}</Error_>}
        </div>
      )}
    </div>
  );
}

function Formulario({
  datos,
  alTerminar,
  alCancelar,
}: {
  datos: { ordenId: string; lineas: LineaDevolvible[]; motivos: { id: string; nombre: string; reentraABodega: boolean }[] };
  alTerminar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [fechaDevolucion, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivoId, setMotivoId] = useState(datos.motivos[0]?.id ?? '');
  const [soporte, setSoporte] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>({});

  const registrar = useMutation({
    mutationFn: () =>
      registrarDevolucion({
        ordenId: datos.ordenId,
        fechaDevolucion,
        motivoId,
        ...(soporte.trim() ? { documentoSoporte: soporte.trim() } : {}),
        lineas: Object.entries(cantidades)
          .filter(([, v]) => v.trim() !== '' && Number(v) > 0)
          .map(([lineaOrdenId, v]) => ({ lineaOrdenId, cantidad: v })),
      }),
    onSuccess: alTerminar,
  });

  const motivo = datos.motivos.find((m) => m.id === motivoId);
  // Solo se ofrecen las líneas que tienen algo devolvible: ofrecer una en cero
  // sería invitar a un error que el servidor va a rechazar de todos modos.
  const devolvibles = datos.lineas.filter((l) => Number(l.disponible) > 0);
  const hayAlgo = Object.values(cantidades).some((v) => v.trim() !== '' && Number(v) > 0);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Registrar una devolución</h3>

      {devolvibles.length === 0 ? (
        <Aviso>Ya se devolvió todo lo que se había recibido de esta orden.</Aviso>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm">
              <span className="block text-xs text-tenue">Cuándo devolvieron</span>
              <input
                type="date"
                value={fechaDevolucion}
                onChange={(e) => setFecha(e.target.value)}
                className="mt-1 min-h-[44px] w-full rounded-md border border-borde px-2 sm:min-h-0 sm:py-1.5"
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-tenue">Por qué</span>
              <select
                value={motivoId}
                onChange={(e) => setMotivoId(e.target.value)}
                className="mt-1 min-h-[44px] w-full rounded-md border border-borde bg-superficie px-2 sm:min-h-0 sm:py-1.5"
              >
                {datos.motivos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-tenue">Soporte (opcional)</span>
              <input
                value={soporte}
                onChange={(e) => setSoporte(e.target.value)}
                placeholder="Nota de devolución"
                className="mt-1 min-h-[44px] w-full rounded-md border border-borde px-2 sm:min-h-0 sm:py-1.5"
              />
            </label>
          </div>

          {/*
            Lo que va a pasar con el inventario se dice ANTES de registrar, no
            después: sale del motivo elegido, y quien registra tiene derecho a
            saber qué está a punto de mover.
          */}
          {motivo && (
            <p className="text-xs text-tenue">
              {motivo.reentraABodega ? (
                <>
                  Con «{motivo.nombre}», el producto <strong>vuelve al inventario</strong> de la
                  bodega.
                </>
              ) : (
                <>
                  Con «{motivo.nombre}», el producto <strong>no vuelve al inventario</strong>.
                </>
              )}
            </p>
          )}

          <div className="space-y-2">
            {devolvibles.map((l) => (
              <div
                key={l.lineaOrdenId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-borde px-3 py-2"
              >
                <div className="text-sm">
                  <div className="font-medium">{l.producto}</div>
                  <div className="text-xs text-tenue">
                    Recibió {cantidad(l.cantidadRecibida)}
                    {Number(l.yaDevuelto) > 0 && ` · ya devolvió ${cantidad(l.yaDevuelto)}`} · quedan{' '}
                    <strong>{cantidad(l.disponible)}</strong>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  max={l.disponible}
                  step="1"
                  value={cantidades[l.lineaOrdenId] ?? ''}
                  onChange={(e) =>
                    setCantidades((c) => ({ ...c, [l.lineaOrdenId]: e.target.value }))
                  }
                  placeholder="0"
                  className="cifra min-h-[44px] w-24 rounded-md border border-borde px-2 text-right sm:min-h-0 sm:py-1.5"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Boton
              deshabilitado={!hayAlgo || motivoId === '' || registrar.isPending}
              onClick={() => registrar.mutate()}
            >
              Registrar la devolución
            </Boton>
            <Boton tipo="secundario" onClick={alCancelar}>
              Cancelar
            </Boton>
          </div>

          {registrar.isError && <Error_>{(registrar.error as Error).message}</Error_>}
        </>
      )}
    </div>
  );
}
